import type {DirectTurnStartPath} from '@github-ui/ahp-session-client/contracts'

import {isOneCopilotBrowserTelemetryEnabled} from './telemetry-gate'

/**
 * Session start state, using Mission Control's activation definitions: `cold_start` provisions new
 * compute, `wake` wakes an idle or offline environment, and `warm` connects to an environment that is
 * already online. Mission Control does not send the activation kind to the browser, so these come
 * from the client's own connection signals.
 */
export const START_TYPES = ['cold_start', 'wake', 'warm', 'unknown'] as const
export type StartType = (typeof START_TYPES)[number]

const MAX_START_TYPES = 512
const startTypesByTask = new Map<string, Exclude<StartType, 'unknown'>>()
const startTypesByTurn = new Map<string, Exclude<StartType, 'unknown'>>()

export function startTypeFromStartPath(startPath: DirectTurnStartPath | undefined): StartType {
  switch (startPath) {
    case 'cold':
      return 'cold_start'
    case 'resume':
      return 'wake'
    case 'active':
      return 'warm'
    default:
      return 'unknown'
  }
}

export function startTypeFromEnvironment({reachable, wakeable}: {reachable?: boolean; wakeable?: boolean}): StartType {
  if (reachable) return 'warm'
  if (wakeable) return 'wake'
  return 'unknown'
}

function remember(map: Map<string, Exclude<StartType, 'unknown'>>, key: string | undefined, type: StartType) {
  if (!key || type === 'unknown' || !isOneCopilotBrowserTelemetryEnabled()) return
  map.delete(key)
  map.set(key, type)
  while (map.size > MAX_START_TYPES) {
    const oldest = map.keys().next().value
    if (!oldest) break
    map.delete(oldest)
  }
}

/** Records how the task's current connection started. A cold start or wake replaces an earlier value. */
export function recordTaskStartType(taskId: string | undefined, type: StartType): void {
  remember(startTypesByTask, taskId, type)
}

/** Records the state seen when a task view first connects, without replacing a known value. */
export function recordInitialTaskStartType(taskId: string | undefined, type: StartType): void {
  if (taskId && startTypesByTask.has(taskId)) return
  remember(startTypesByTask, taskId, type)
}

export function recordTurnStartType(turnId: string | undefined, type: StartType): void {
  remember(startTypesByTurn, turnId, type)
}

export function startTypeFor(fact: {turn_id?: string; task_id?: string}): StartType {
  return (
    (fact.turn_id && startTypesByTurn.get(fact.turn_id)) ||
    (fact.task_id && startTypesByTask.get(fact.task_id)) ||
    'unknown'
  )
}

export function clearStartTypes(): void {
  startTypesByTask.clear()
  startTypesByTurn.clear()
}
